import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import urlJoin from 'url-join';
import z from 'zod';
import { privateHttpClient } from '../httpClient';
import { createApiResponseSchema } from '../utils';
import { campaignsApiPath } from './apiPath';
import {
  CampaignGoodsSlotSchema,
  RegisteredContentSchema,
  SeedingContentChannelSchema,
  SeedingFulfillmentStatusSchema,
  SeedingParticipationStatusSchema,
} from './getSeedingMeRecruitment.api';

/** 4탭 분류 (PENDING=제안, CONFIRMED=수락, COMPLETED=완료, REJECTED=거절) */
export const SeedingProposalStatusSchema = z.enum([
  'PENDING',
  'CONFIRMED',
  'COMPLETED',
  'REJECTED',
]);
export type SeedingProposalStatus = z.infer<typeof SeedingProposalStatusSchema>;

const GetSeedingMeProposalRequestSchema = z
  .object({
    searchStatus: SeedingProposalStatusSchema,
    page: z.number().min(1).optional(),
    size: z.number().min(1).max(100).optional(),
  })
  .describe('GetSeedingMeProposalRequestSchema');

export type GetSeedingMeProposalRequest = z.infer<typeof GetSeedingMeProposalRequestSchema>;

const SeedingProposalItemSchema = z
  .object({
    participationId: z.number(),
    campaignId: z.number(),
    searchStatus: SeedingProposalStatusSchema,
    participationStatus: SeedingParticipationStatusSchema,
    goodsImage: z.string(),
    /** 브랜드 코드(이벤트 로그 brand_id) */
    brandId: z.string(),
    brandName: z.string(),
    /** 캠페인명(이벤트 로그 campaign_title) */
    title: z.string(),
    goodsName: z.string(),
    goodsPrice: z.number(),
    snsChannels: z.array(SeedingContentChannelSchema),
    /** 타이머 종료 — 제안=수락 마감(48h), 수락=콘텐츠 제출 마감 */
    timerEndsAt: z.string().nullable(),
    fulfillmentStatus: SeedingFulfillmentStatusSchema.nullable(),
    /** 제안 응답 마감일 (제안 받은 시점 +48h) */
    responseDeadlineAt: z.string().nullable(),
    /** 거절 사유 (거절 탭에서만 노출) */
    rejectionReasons: z.array(z.string()).nullable(),
    messageToBrand: z.string().nullable(),
    /** 제안 받은 일시 */
    createdAt: z.string(),
    /** 콘텐츠 등록 이력 — COMPLETED 탭에서만 채워짐, 그 외 탭은 빈 배열 */
    registeredContents: z.array(RegisteredContentSchema),
    /** 캠페인의 시딩 상품 슬롯 목록 — 모든 탭에서 항상 채움. "외 N개" = goodsSlots.length - 1 */
    goodsSlots: z.array(CampaignGoodsSlotSchema),
  })
  .describe('SeedingProposalItemSchema');

const GetSeedingMeProposalResponseDataSchema = z.object({
  list: z.array(SeedingProposalItemSchema),
});

/** 응답 envelope의 link — next=다음 페이지 URL(없으면 null), total=총 건수 */
const PageLinkSchema = z.object({
  next: z.string().nullable(),
  total: z.number(),
});

const GetSeedingMeProposalResponseSchema = createApiResponseSchema(
  GetSeedingMeProposalResponseDataSchema,
).extend({
  link: PageLinkSchema.optional(),
});

export type GetSeedingMeProposalResponse = z.infer<typeof GetSeedingMeProposalResponseSchema>;

export const getSeedingMeProposal = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(GetSeedingMeProposalRequestSchema),
    async (params: GetSeedingMeProposalRequest): Promise<GetSeedingMeProposalResponse> =>
      executeStandardRequest(async () => {
        const response = await privateHttpClient.get<GetSeedingMeProposalResponse>(
          urlJoin(campaignsApiPath.getSeedingMeProposal),
          params,
        );
        return response;
      }),
    validateApiResponse(GetSeedingMeProposalResponseSchema),
  ),
);
